import React, { useState } from 'react';
import { logUserActivityQuietly } from './supabaseClient';

function PhenobarbitoneCalculator({user}) {
    const [patientGroup, setPatientGroup] =useState ('adult_neonate');
    const [weightNum, setWeightNum] = useState('');
    const [measuredCss, setMeasuredCss] = useState('');
    const [currentDose, setCurrentDose] = useState('');
    const [interval, setInterval] = useState('');
    const [simDose, setSimDose] = useState('');
   

    const PB_CONSTANTS = {
        pediatric: 0.008,
        adult_neonate: 0.004,
        elderly: 0.003
    };

    // Step 1: Calculate CL (L/hour)
    const clValue = PB_CONSTANTS[patientGroup] * weightNum;

    // Step 2: Predicted Css (The 'Goal')
    // Formula: Css = (Dose * S * F) / (CL * Interval)
    // Note: Interval is usually 24 for daily dosing
    const S = 0.91
    const F = 1
    const predictedCss =clValue > 0 && interval > 0 ? (Number(currentDose) * S * F) / (clValue * Number(interval)):0;


    // Step 3 Logic: Comparison
    const measured = measuredCss >0 ?Number(measuredCss):0;
    // Calculate how much the actual level differs from the prediction (%)
    const deviation = predictedCss > 0 ? ((measured - predictedCss) / predictedCss) * 100 : 0;

    let comparisonStatus = {
        text: "Within Range",
        color: "#2ecc71",
        note: "Patient is responding as expected for an average adult/pediatric profile."
    };

    if (measured < predictedCss * 0.8) {
        comparisonStatus = {
            text: "Below Expectation",
            color: "#3498db",
            note: `Actual level is ${Math.abs(deviation).toFixed(0)}% lower than predicted. Check for non-compliance, malabsorption, or early sampling.`
        };
    } else if (measured > predictedCss * 1.2) {
        comparisonStatus = {
            text: "Above Expectation",
            color: "#e67e22",
            note: `Actual level is ${deviation.toFixed(0)}% higher than predicted. Check for renal/hepatic impairment or enzyme inhibition.`
        };
    }

    // === RE-ENGINEERED DOSE SIMULATION CORE (INDIVIDUAL HIGH-PRECISION) ===
    const simDoseNum = Number(simDose);
    const currentDoseNum = Number(currentDose) || 0;
    const measuredNum = Number(measuredCss) || 0;
    const intervalNum = Number(interval) || 0;
    const currentTotalDailyDose = Number(currentDoseNum/(interval/24))
    
    let simulatedResult = null;

    if (simDoseNum > 0 && currentDoseNum > 0 && measuredNum > 0) {
        // Direct individual patient ratio scaling
        simulatedResult = (simDoseNum / (currentDoseNum/(interval/24))) * measuredNum;
    } else if (simDoseNum > 0 && intervalNum > 0 && clValue > 0) {
        // Fallback to population constants ONLY if no measured lab baseline exists yet
        simulatedResult = (simDoseNum * 0.91 * 1) / (clValue * intervalNum);
    }

    // Logic for Recommendation
    let recColor = "#2ecc71";
    let recTitle = "THERAPEUTIC";
    let recAction = "Level within therapeutic range (15-40 mg/L). If response is poor but compliance good, consider a small dose increment (do not exceed max). If response is good, continue current dose.";

    if (measured > 0) {
        if (measured < 15) {
            recColor = "#3498db";
            recTitle = "SUBTHERAPEUTIC";
            recAction = simDoseNum > 0 
                ? `If response is poor but compliance is good, increase dose up to ${simDoseNum}mg ${interval}hrly to reach an estimated level of ${simulatedResult?.toFixed(1)} mg/L.and re-sample. If response is good, continue current dose.`
                : "Level is sub-therapeutic (< 15 mg/L). If response is poor, Check compliance first before consider a dose increment. If response is good, continue current dose.";
        } else if (measured > 40) {
            recColor = "#e74c3c";
            recTitle = "TOXIC RANGE";
            recAction = "Level is TOXIC (> 40 mg/L). Withhold dose and monitor for clinical signs of toxicity (e.g., CNS depression and respiratory distress). Reduce dose or withhold temporarily. Review dose once level falls.";
        }
    }
    // Add this logic before the return
    const getClinicalInsight = () => {
        if (!measured || !predictedCss) return null;
        
        const actual = Number(measured);
        const estimate = Number(predictedCss);
        
        if (actual > estimate * 1.2) { // More than 20% higher than estimate
            return {
                title: "Actual > Population Estimate",
                reason: "Patient may be a 'Slow Metabolizer' or taking an inhibitor (e.g.Carbonic Anhydrase inhibitor, Chloramphenicol, Clarithromycin, Hydroxyzine, Magnesium Sulphate, Methylphenidate, Phenytoin, Primidone, Quinine, Valproic Acid and Derivative.).",
                color: "#e67e22"
            };
        } else if (actual < estimate * 0.8) { // More than 20% lower than estimate
            return {
                title: "Actual < Population Estimate",
                reason: "Patient may be a 'Fast Metabolizer', have poor absorption, or a compliance issue. Also consider induction by other drugs (e.g. Amphetamine, Cholestyramine Resin, Folic Acid, Ketorolac, Leucovorin Calcium, Mefloquine, Multivitamins/ Minerals, Orlistat, Pyridoxine, Rifamycin derivative, Tipranavir).",
                color: "#3498db"
            };
        }
        return {
            title: "Matches Population Estimate",
            reason: "Patient's metabolism is consistent with standard population parameters.",
            color: "#2ecc71"
        };
    };
    const insight = getClinicalInsight();

    return (
        <section style={{ marginBottom: '20px', padding: '15px', background: '#1e272e', borderRadius: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
                <button 
                    type="button"
                    className="no-print" 
                    onClick={() => {
                                    const actualUserEmail = user && user.email ? user.email : 'anonymous-user';
                                    const trackingLabel = `${actualUserEmail}-printed-Phenobarbitone`;
                                    logUserActivityQuietly(trackingLabel); 
                                    window.print();
                                }} 
                    style={{
                    padding: '10px 20px',
                    backgroundColor: '#27ae60', 
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.2)'
                    }}
                >
                    🖨️ Print PDF Report
                </button>
            </div>
            <h2 style={{ textAlign: 'center', color: '#3498db', marginTop: 0 }}>Phenobarbitone TDM Worksheet</h2>
            <details style={{
                backgroundColor: '#1E293B',
                border: '1px solid #38BDF8',
                borderRadius: '8px',
                padding: '10px 15px',
                marginBottom: '20px',
                cursor: 'pointer'
            }}>
                <summary style={{ color: '#38BDF8', fontWeight: 'bold' }}>
                ⏱️ Check Clinical Sampling Protocol (Click to expand)
                </summary>
                <div style={{ marginTop: '12px', padding: '15px', background: 'rgba(0,0,0,0.15)', borderRadius: '8px', border: '1px solid #3d4e5d', fontSize: '0.9rem', color: '#F8FAFC' }}>
    
                    {/* SECTION 1: SAMPLING TIMELINE MATRIX TABLE */}
                    <div style={{ marginBottom: '15px' }}>
                        <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#00d2ff', textTransform: 'uppercase', display: 'block', marginBottom: '6px', letterSpacing: '0.5px' }}>
                            ⏱️ Kinetic Accumulation & Stabilization Windows
                        </span>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.85rem' }}>
                            <tbody>
                                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                                    <td style={{ padding: '8px 0', fontWeight: 'bold', color: '#fff', width: '38%' }}>Without Loading Dose:</td>
                                    <td style={{ padding: '8px 0', color: '#bdc3c7', lineHeight: '1.4' }}>
                                        Draw steady-state levels **2 – 4 weeks** after initiation or any change in the maintenance regimen (Required due to extreme half-life).
                                    </td>
                                </tr>
                                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                                    <td style={{ padding: '8px 0', fontWeight: 'bold', color: '#fff' }}>With Loading Dose (LD):</td>
                                    <td style={{ padding: '8px 0', color: '#bdc3c7' }}>
                                        Draw levels **2 – 3 hours** immediately following the complete administration of the load.
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ padding: '8px 0', fontWeight: 'bold', color: '#fff' }}>Pre-Sample Draw:</td>
                                    <td style={{ padding: '8px 0', color: '#bdc3c7' }}>Draw a true trough sample **just before** the next scheduled dose (Applies to both IV/Oral routes).</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {/* SECTION 2: TARGET REFERENCE WINDOW */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px', marginBottom: '15px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                        {/* Therapeutic Range Card */}
                        <div style={{ background: 'rgba(46, 204, 113, 0.06)', border: '1px solid rgba(46, 204, 113, 0.2)', padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
                            <div style={{ fontWeight: 'bold', color: '#2ecc71', fontSize: '0.8rem', textTransform: 'uppercase' }}>🎯 Therapeutic Target Range:</div>
                            <div style={{ fontSize: '1.4rem', fontWeight: 'bold', marginTop: '4px', color: '#fff' }}>
                                15 - 40 <span style={{ fontSize: '0.85rem', fontWeight: 'normal', color: '#bdc3c7' }}>mg/L</span>
                            </div>
                        </div>
                    </div>

                    {/* SECTION 3: SEVERE TOXICITY CRITICAL ALERTS */}
                    <div style={{ background: 'rgba(231, 76, 60, 0.08)', borderLeft: '4px solid #e74c3c', padding: '10px', borderRadius: '0 6px 6px 0' }}>
                        <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#e74c3c', textTransform: 'uppercase', display: 'block' }}>
                            ⚠️ Severe Toxicity Alert Threshold
                        </span>
                        <div style={{ fontSize: '0.85rem', color: '#ecf0f1', marginTop: '4px', lineHeight: '1.4' }}>
                            • Sedation, drowsiness, and mild ataxia are common at higher therapeutic ranges.<br />
                            • <strong style={{ color: '#ff7675' }}>SEVERE SYSTEMIC TOXICITY</strong> correlates strongly with serum concentrations <strong>&gt; 60 mg/L</strong> (Risk of profound central nervous system depression, respiratory depression, hypothermia, and cardiovascular instability).
                        </div>
                    </div>

                </div>
            </details>
            <h4 style={{ color: '#3498db', marginTop: 0 }}>Step 1: Clearance Calculation</h4>
            
            <label style={{ fontSize: '0.8rem', color: '#bdc3c7' }}>Patient Category:</label>
            <div style={{ display: 'flex', gap: '8px', margin: '10px 0' }}>
                <button onClick={() => setPatientGroup('pediatric')} style={btnStyle(patientGroup === 'pediatric')}>Pediatric</button>
                <button onClick={() => setPatientGroup('adult_neonate')} style={btnStyle(patientGroup === 'adult_neonate')}>Adult/Neonate</button>
                <button onClick={() => setPatientGroup('elderly')} style={btnStyle(patientGroup === 'elderly')}>Elderly ( &gt;65y)</button>
            </div>
            <div>
                <label style={{ fontSize: '0.75rem',width:'70%', color: '#bdc3c7' }}>Weight (Kg):</label>
                <input type="number" value={weightNum} onChange={(e)=>{const val = e.target.value; setWeightNum(val === "" ? "" : Number(val))}} style={inputStyle} />
            </div>

            <div style={{ padding: '10px', background: 'rgba(0,0,0,0.2)', borderRadius: '5px' }}>
                <div style={{ fontSize: '0.7rem', color: '#95a5a6', marginTop: '5px' }}>
                    Working: {PB_CONSTANTS[patientGroup]} L/kg/hr × {weightNum} kg
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{color:'#f1c40f'}}>Calculated CL:</span>
                    <span style={{ fontWeight: 'bold', color: '#f1c40f' }}>{clValue.toFixed(3)} L/hr</span>
                </div>
            </div>
            <h4 style={{ color: '#3498db', marginTop: 0 }}>Step 2: Estimate Css from Current Dose</h4>
    
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                    <label style={{ fontSize: '0.75rem', color: '#bdc3c7', display: 'block', marginBottom: '5px' }}>
                        Current Dose (mg):
                    </label>
                    <input 
                        type="number" 
                        value={currentDose} 
                        onChange={(e) => setCurrentDose(e.target.value)} 
                        style={{ width: '80%', padding: '10px', background: '#2c3e50', border: '1px solid #34495e', color: 'white', borderRadius: '5px' }} 
                    />
                </div>
                <div>
                    <label style={{ fontSize: '0.75rem', color: '#bdc3c7', display: 'block', marginBottom: '5px' }}>
                        Interval (hours):
                    </label>
                    <input 
                        type="number" 
                        value={interval} 
                        onChange={(e) => setInterval(e.target.value)} 
                        style={{ width: '80%', padding: '10px', background: '#2c3e50', border: '1px solid #34495e', color: 'white', borderRadius: '5px' }} 
                    />
                </div>
            </div>
            {/* Result Display for Step 2 */}
            
            <div style={{ textAlign: 'center',color:'white', padding: '10px', background: 'rgba(0,0,0,0.2)', borderRadius: '4px' }}>
                {/* Math Structure */}
                <div style={{ display: 'inline-block', verticalAlign: 'middle', textAlign: 'right', paddingRight: '10px' }}>
                    Predicted Steady-State (Css) =
                </div>
                <div style={{ display: 'inline-block', verticalAlign: 'middle', textAlign: 'center' }}>
                    <div style={{ borderBottom: '1px solid white', paddingBottom: '2px' }}>
                        Current Dose ({currentDose || 0}mg × 0.91 × 1)
                    </div>
                    <div style={{ paddingTop: '2px' }}>
                        ({clValue.toFixed(3)} L/hr × {interval}hr)
                    </div>
                </div>
                <div style={{ fontSize: '0.9remrem',textAlign: 'left', fontWeight: 'bold', color: '#86d12c',marginTop:'20px' }}>
                    Predicted Css: {predictedCss.toFixed(2)} <span style={{fontSize: '0.8rem'}}>mg/L</span>
                </div>
            </div>
            
            <h4 style={{ color: '#3498db', marginTop: '20px' }}>Step 3: Clinical Correlation</h4>
    
            <div>
                <label style={{ fontSize: '0.75rem',width:'70%', color: '#bdc3c7' }}>Lab Measured Level (mg/L):</label>
                <input type="number" value={measuredCss} onChange={(e)=>{const val = e.target.value; setMeasuredCss(val === "" ? "" : Number(val))}} style={inputStyle} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-around',marginTop:'20px', background: '#2c3e50', padding: '15px', borderRadius: '8px' }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.7rem', color: '#95a5a6', textTransform: 'uppercase' }}>Predicted Css</div>
                    <div style={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#ecf0f1' }}>
                        {predictedCss.toFixed(2)} <span style={{fontSize: '0.8rem'}}>mg/L</span>
                    </div>
                </div>

                <div style={{ width: '1px', background: '#34495e' }}></div>

                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.7rem', color: '#95a5a6', textTransform: 'uppercase' }}>Measured Level</div>
                    <div style={{ fontSize: '1.4rem', fontWeight: 'bold', color: measuredCss > 40 ? '#e74c3c' : '#2ecc71' }}>
                        {measuredCss} <span style={{fontSize: '0.8rem'}}>mg/L</span>
                    </div>
                </div>
            </div>

            {measured > 0 && (
                
                <div style={{ padding: '15px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px', borderLeft: `6px solid ${comparisonStatus.color}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                        <span style={{ fontWeight: 'bold', color: comparisonStatus.color }}>{comparisonStatus.text}</span>
                        <span style={{ fontSize: '0.8rem', color: '#95a5a6'}}>Dev: {deviation > 0 ? '+' : ''}{deviation.toFixed(1)}%</span>
                    </div>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#bdc3c7', lineHeight: '1.4' }}>
                        {comparisonStatus.note}
                    </p>
                </div>
            )}
            {/* STEP 4: DOSE SIMULATION (INDIVIDUAL HIGH-PRECISION PATIENT MODEL) */}
            <h4 style={{ margin: '0 0 10px 0', color: '#3498db', fontSize: '0.9rem', marginTop: '20px' }}>
                Step 4: Dose Simulation
            </h4>
            
            <div style={{ display: 'flex', gap: '15px', alignItems: 'center', marginBottom: '15px' }}>
                <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '0.7rem', color: '#95a5a6', display: 'block', marginBottom: '4px' }}>NEW PROPOSED TOTAL DAILY DOSE (mg/day)</label>
                    <input 
                        type="number" 
                        value={simDose}
                        onChange={(e) => setSimDose(e.target.value)}
                        placeholder="e.g. 90"
                        style={{ width: '100%', padding: '8px', background: '#1b1f23', border: simDose ? '1px solid #3498db' : '1px solid #34495e', color: 'white', borderRadius: '4px', boxSizing: 'border-box' }}
                        disabled={!measuredCss || !currentDose}
                    />
                </div>
                
                <div style={{ flex: 1, textAlign: 'center' }}>
                    <div style={{ fontSize: '0.7rem', color: '#95a5a6' }}>EST. NEW CSS</div>
                    <div style={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#00d2ff' }}>
                        {simulatedResult ? simulatedResult.toFixed(1) : '--'} <span style={{ fontSize: '0.7rem', fontWeight: 'normal', color: '#95a5a6' }}>mg/L</span>
                    </div>
                </div>
            </div>

            {/* VISUAL SPECTRUM PROGRESS SLIDER TRACK BAR */}
            {simulatedResult > 0 && (
                <div style={{ padding: '15px', background: '#111416', borderRadius: '8px', border: '1px solid #2c3539', marginBottom: '15px' }}>
                    <div style={{ position: 'relative', height: '14px', background: '#2c3e50', borderRadius: '7px', margin: '15px 0 15px 0', overflow: 'visible' }}>
                        
                        {/* TARGET THERAPEUTIC REGION SHADOW BOUNDARY HIGHLIGHT (15.0 to 40.0 mg/L Highlight Track Zone) */}
                        {/* Maps across a 0 to 60 mg/L scale context landscape */}
                        <div style={{ 
                            position: 'absolute', 
                            left: '25%',  /* 15 mg/L is exactly 25% of 60 mg/L max scale */
                            width: '41.67%', /* (40 - 15) / 60 = 41.67% wide span zone */
                            height: '100%', 
                            background: 'rgba(46, 204, 113, 0.22)', 
                            borderLeft: '1px dashed #2ecc71', 
                            borderRight: '1px dashed #2ecc71',
                            borderRadius: '4px'
                        }} />

                        {/* ACTIVE PATIENT PINPOINT INDICATOR NODE (Glowing yellow indicator bead node) */}
                        <div style={{
                            position: 'absolute',
                            left: `${Math.max(0, Math.min(98, (simulatedResult / 60) * 100))}%`,
                            transform: 'translateX(-50%)',
                            width: '18px',
                            height: '18px',
                            top: '-2px',
                            background: '#f1c40f',
                            borderRadius: '50%',
                            boxShadow: '0 0 12px #f1c40f',
                            border: '2px solid #fff',
                            transition: 'left 0.2s ease-out',
                            zIndex: 10
                        }} />
                    </div>

                    {/* GRAPH SCALING X-AXIS SCALE FOOTER LABELS */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.62rem', color: '#7f8c8d', padding: '0 2px', marginBottom: '12px' }}>
                        <span>0 mg/L</span>
                        <span style={{ color: '#2ecc71', fontWeight: 'bold' }}>Target Spec Window: 15.0 - 40.0 mg/L</span>
                        <span>60 mg/L</span>
                    </div>

                    {/* CLEAR CALCULATION AUDIT VERIFICATION LABELS */}
                    <div style={{ fontSize: '0.68rem', color: '#57606f', borderTop: '1px solid rgba(255,255,255,0.03)', paddingTop: '8px', lineHeight: '1.4', textAlign: 'left' }}>
                        {measuredNum > 0 ? (
                            <>
                                Formula Predict Css: (Proposed New Dose / Current Daily Dose) × Actual Measured Lab Level<br />
                                Individual Patient Linear Ratio Trace: ({simDose || 0} mg / {currentTotalDailyDose} mg) × {measuredCss} mg/L = {simulatedResult.toFixed(1)} mg/L
                            </>
                        ) : (
                            <>
                                Formula Predict Css: (Dose × S × F) / (Clearance Value × Dosing Interval)<br />
                                Population Estimate Reference Trace: ({simDose || 0}mg × 0.91 × 1) / ({clValue.toFixed(3)} L/hr × {interval}hr)
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* CLINICAL RECOMMENDATION CARD PANEL */}
            <div style={{ 
                marginTop: '20px', 
                padding: '15px', 
                background: '#1b1f23', 
                borderRadius: '8px', 
                borderLeft: `6px solid ${recColor}`,
                boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
                marginBottom: '15px'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <h4 style={{ margin: 0, color: recColor, textTransform: 'uppercase', fontSize: '0.85rem' }}>
                        Final Recommendation
                    </h4>
                    <span style={{ fontSize: '0.75rem', color: '#95a5a6' }}>Status: {recTitle}</span>
                </div>

                <div style={{ fontSize: '0.95rem', color: '#ecf0f1' }}>
                    <strong>Plan:</strong> {recAction}
                </div>

                <div style={{ marginTop: '10px', fontSize: '0.75rem', color: '#7f8c8d', fontStyle: 'italic' }}>
                    Note: TDM should be repeated in 10-14 days due to Phenobarbitone's long half-life.
                </div>
            </div>

            {insight && (
                <div style={{ 
                    marginTop: '15px', 
                    padding: '12px', 
                    background: '#1b1f23', 
                    borderRadius: '8px', 
                    borderLeft: `5px solid ${insight.color}`,
                    marginBottom: '15px'
                }}>
                    <h5 style={{ margin: '0 0 5px 0', color: insight.color, fontSize: '0.9rem' }}>
                        🔍 Interpretation: {insight.title}
                    </h5>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#bdc3c7', lineHeight: '1.4' }}>
                        {insight.reason}
                    </p>
                </div>
            )}

            {/* HANDBOOK DISCLAIMER LEGEND FOOTER FOR MEDICAL FILING */}
            <p style={{ margin: '12px 0 0 0', fontSize: '0.62rem', color: '#7f8c8d', fontStyle: 'italic', textAlign: 'center', lineHeight: '1.3' }}>
                *Evaluation derived from official Clinical Pharmacokinetics Handbook criteria. Patient-specific clinical conditions and absolute biomarker trends must always override automated population estimates prior to final intervention execution.
            </p>
        </section>

        
    );
}
const inputStyle = {
    width: '60%',
    padding: '8px',
    marginTop: '5px',
    borderRadius: '4px',
    border: '1px solid #ccc',
    background: 'white',
    color: 'black'
};
const btnStyle = (active) => ({
    padding: '8px 12px',
    border: 'none',
    borderRadius: '4px',
    background: active ? '#f1c40f' : '#34495e',
    color: 'white',
    cursor: 'pointer',
    flex: 1
});

export default PhenobarbitoneCalculator;